import { Utils } from "../../Utils"
import SmallLicense from "../../Models/smallLicense"

interface ExtMetadataProp {
    value: string
    source: string
    hidden: string
}

interface TemplateQueryAPIResponse {
    batchcomplete: string
    query: {
        normalized?: {
            from: string
            to: string
        }[]
        pages: {
            [key: string]: {
                pageid: number
                ns: number
                title: string
                templates?: {
                    ns: number
                    title: string
                }[]
            }
        }
    }
}
// Map license names of Wikimedia Commons to different names
const licenseMapping = {}

// Map template names to license names
const templateMapping = {
    "Template:PD": "Public Domain",
    "Template:CC0": "CC0 1.0",
}
export interface ImageQueryAPIResponse {
    continue: {
        iistart: string
        continue: string
    }
    query: {
        normalized?: {
            from: string
            to: string
        }[]
        pages: {
            [key: string]: {
                pageid: number
                ns: number
                title: string
                imagerepository: string
                imageinfo?: {
                    user: string
                    url: string
                    descriptionurl: string
                    descriptionshorturl: string
                    extmetadata?: {
                        DateTime: ExtMetadataProp
                        ObjectName: ExtMetadataProp
                        CommonsMetadataExtension?: ExtMetadataProp
                        Categories?: ExtMetadataProp
                        Assessments?: ExtMetadataProp
                        ImageDescription?: ExtMetadataProp
                        DateTimeOriginal?: ExtMetadataProp
                        Credit?: ExtMetadataProp
                        Artist?: ExtMetadataProp
                        LicenseShortName?: ExtMetadataProp
                        UsageTerms?: ExtMetadataProp
                        AttributionRequired?: ExtMetadataProp
                        Copyrighted?: ExtMetadataProp
                        Restrictions?: ExtMetadataProp
                        License?: ExtMetadataProp
                    }
                }[]
            }
        }
    }
}

export default class Wikimedia {
    /**
     * Recursively walks a wikimedia commons category in order to search for entries, which can be File: or Category: entries
     * Returns (a promise of) a list of URLS
     * @param categoryName The name of the wikimedia category
     * @param maxLoad: the maximum amount of images to return
     * @param continueParameter: if the page indicates that more pages should be loaded, this uses a token to continue. Provided by wikimedia
     */
    public static async getCategoryContents(
        categoryName: string,
        maxLoad = 10,
        continueParameter: string = undefined
    ): Promise<string[]> {
        if (categoryName === undefined || categoryName === null || categoryName === "") {
            return []
        }
        if (!categoryName.startsWith("Category:")) {
            categoryName = "Category:" + categoryName
        }

        let url =
            "https://commons.wikimedia.org/w/api.php?" +
            "action=query&list=categorymembers&format=json&" +
            "&origin=*" +
            "&cmtitle=" +
            encodeURIComponent(categoryName)
        if (continueParameter !== undefined) {
            url = `${url}&cmcontinue=${continueParameter}`
        }
        const response = await Utils.downloadJson(url)
        const members = response["query"]?.categorymembers ?? []
        const imageOverview: string[] = members.map((member) => member.title)

        if (response["continue"] === undefined) {
            // We are done crawling through the category - no continuation in sight
            return imageOverview
        }

        if (maxLoad - imageOverview.length <= 0) {
            console.debug(`Recursive wikimedia category load stopped for ${categoryName}`)
            return imageOverview
        }

        // We do have a continue token - let's load the next page
        const recursive = await Wikimedia.getCategoryContents(
            categoryName,
            maxLoad - imageOverview.length,
            response["continue"].cmcontinue
        )
        imageOverview.push(...recursive)
        return imageOverview
    }
    private static removeLinks(text: string): string {
        // Remove <a> tags
        return text.replace(/<a.*?>(.*?)<\/a>/g, "$1")
    }

    /**
     * Queries the wikimedia api and tries to construct a license based on the response
     * @param baseUrl
     * @param filename
     */
    static async getLicenseFor(baseUrl: string, filename: string): Promise<SmallLicense> {
        let cors = ""
        if (!Utils.runningFromConsole) {
            // https://commons.wikimedia.org/w/api.php?action=help&modules=main
            cors = "&origin=*"
        }
        const apiUrl = `${baseUrl}/w/api.php?action=query&format=json&prop=imageinfo&iiprop=url|extmetadata|user&iimetadataversion=latest&titles=${filename}${cors}`
        const response = await fetch(apiUrl)
        const apiDetails: ImageQueryAPIResponse = await response.json()
        const wikiPage = apiDetails.query.pages[Object.keys(apiDetails.query.pages)[0]]

        const fileUrl = wikiPage.imageinfo[0].url
        const wikiUrl = wikiPage.imageinfo[0].descriptionurl
        const author =
            wikiPage.imageinfo[0].extmetadata?.Artist?.value || wikiPage.imageinfo[0].user
        let license = wikiPage.imageinfo[0].extmetadata?.LicenseShortName?.value || null
        // Check if the license is present in the response
        if (!license) {
            console.log(`${filename} does not have a license, falling back to checking template...`)
            const templateUrl = `${baseUrl}/w/api.php?action=query&format=json&prop=templates&titles=${filename}&tllimit=500`
            const templateResponse = await fetch(templateUrl)
            const templateDetails: TemplateQueryAPIResponse = await templateResponse.json()

            // Loop through all templates and check if one of them is a license
            const wikiPage =
                templateDetails.query.pages[Object.keys(templateDetails.query.pages)[0]]
            if (!wikiPage.templates) {
                return undefined
            }
            for (const template of wikiPage.templates) {
                if (templateMapping[template.title]) {
                    console.log(`Found license ${templateMapping[template.title]} for ${filename}`)
                    license = templateMapping[template.title]
                }
            }
        }

        // If no license was found, skip the file
        if (!license) {
            // Log in yellow
            console.log(`\x1b[33m%s\x1b[0m`, `No license found for ${filename}...`)
            return undefined
        }

        const cleanFileName = unescape(filename).replace("File:", "")
        return <SmallLicense>{
            path: cleanFileName,
            license: licenseMapping[license] || license.replace("CC BY", "CC-BY"),
            authors: [Wikimedia.removeLinks(author)],
            sources: [wikiUrl],
        }
    }
}
